import { useMemo } from 'react'
import { type AreaData, type AreaRunHistoryEntry, isDefined, isNotDefined, type LatLng } from '@terros/common'
import { useAllAreas } from './list'
import { toAreaRunInputs } from './areaRunInput'

export type AreaRun = {
  area: AreaData
  run: AreaRunHistoryEntry
}

/** The area run stamped at `timestamp`; areas keep their last 20 runs */
const findAreaRun = (areas: AreaData[], timestamp: number): AreaRun | undefined =>
  areas
    .flatMap((area) => (area.runHistory ?? []).map((run) => ({ area, run })))
    .find(({ run }) => run.timestamp === timestamp)

type UseAreaRunProps = {
  timestamp: number | undefined
  /** The property's location; only areas covering it can have assigned it */
  latlng: LatLng | undefined
}

export const useAreaRun = ({ timestamp, latlng }: UseAreaRunProps): AreaRun | undefined => {
  const enabled = isDefined(timestamp) && isDefined(latlng)
  const inputs = isDefined(latlng) ? toAreaRunInputs(latlng) : []
  const { data: areas } = useAllAreas(inputs[0] ?? {}, enabled)
  const hasWrappedBox = isDefined(inputs[1])
  const { data: wrappedAreas } = useAllAreas(inputs[1] ?? {}, enabled && hasWrappedBox)
  return useMemo(() => {
    if (isNotDefined(timestamp) || isNotDefined(areas)) return
    return findAreaRun([...areas, ...(hasWrappedBox ? wrappedAreas : [])], timestamp)
  }, [areas, wrappedAreas, hasWrappedBox, timestamp])
}
